export type ContractType = 'new' | 'current';

export interface ContractFormData {
	// Shared fields (both new and current clients)
	customerName: string;
	customerTitle: string;
	customerEmail: string;
	customerAddress: string;
	companyName: string;
	repFirstName: string;
	repLastName: string;
	managerEmail: string;
	urgency: string;
	hipaaCompliance: boolean;
	additionalNotes?: string;
	isSuccessTeam?: boolean; // Success team flag for template selection

	// Current client specific fields
	adminLink?: string;
	currentLevitatePlan?: string; // What plan the client currently has with Levitate
	renewalDate?: string;
	monthsLeft?: number;
	billingCycle?: string; // Annual, Quarterly, or Monthly
	contractTypeValue?: string; // The actual contract type like "Website Upgrade"
	totalPriceDueToday?: number; // Total price due today
	totalPriceUponRenewal?: number; // Total price upon renewal (core + website)
	discountBreakdown?: number; // Discount amount for current clients
	fbAdsChecked?: boolean;
	fbPremiumChecked?: boolean;
	gLSAChecked?: boolean;
	customContentChecked?: boolean;
	customContentNonprofitChecked?: boolean;
	handwritten?: boolean;
	pendingCancel?: string;
}

export interface ContractGenerationResponse {
	success: boolean;
	slideUrl?: string;
	fileId?: string;
	fileName?: string;
	pdfUrl?: string;
	error?: string;
}

export const MANAGER_OPTIONS = [
	{ email: 'cady.willard@levitateapp.com', name: 'Cady Willard', successTeamOnly: false },
	{ email: 'casey.brady@levitateapp.com', name: 'Casey Brady', successTeamOnly: false },
	{ email: 'ethan.deal@levitateapp.com', name: 'Ethan Deal', successTeamOnly: false },
	{ email: 'ivan.tomich@levitateapp.com', name: 'Ivan Tomich', successTeamOnly: false },
	{ email: 'leia.stallings@levitateapp.com', name: 'Leia Stallings', successTeamOnly: true },
	{ email: 'maggie.robertson@levitateapp.com', name: 'Maggie Robertson', successTeamOnly: false },
	{ email: 'nick.cristinziano@levitateapp.com', name: 'Nick Cristinziano', successTeamOnly: false },
	{ email: 'nick.freeh@levitateapp.com', name: 'Nick Freeh', successTeamOnly: false },
	{ email: 'wade.naziri@levitateapp.com', name: 'Wade Naziri', successTeamOnly: false },
] as const;

export const CURRENT_PLAN_OPTIONS = [
	{ value: '$150 - Business Plus', label: '$150 - Business Plus' },
	{ value: '$199 - Business Silver', label: '$199 - Business Silver' },
	{ value: '$249 - Business Gold', label: '$249 - Business Gold' },
	{ value: '$299 - Client Happiness Platform', label: '$299 - Client Happiness Platform' },
	{ value: '$349 - Client Happiness Platform w/ AI', label: '$349 - Client Happiness Platform w/ AI' },
	{ value: '$399 - Client Happiness Advanced', label: '$399 - Client Happiness Advanced' },
	{ value: '$299 - Website Essential Standalone', label: '$299 - Website Essential Standalone' },
	{ value: '$349 - Website Preferred Standalone', label: '$349 - Website Preferred Standalone' },
	{ value: '$399 - Website Prestige Standalone', label: '$399 - Website Prestige Standalone' },
	{ value: 'Levitate Essential', label: 'Levitate Essential' },
	{ value: 'Levitate Preferred', label: 'Levitate Preferred' },
	{ value: 'Levitate Prestige', label: 'Levitate Prestige' },
] as const;

export const CONTRACT_TYPE_OPTIONS = [
	{ value: '$249 Website Essential Upgrade', label: '$249 Website Essential Upgrade', successTeamOnly: false },
	{ value: '$299 Website Preferred Upgrade', label: '$299 Website Preferred Upgrade', successTeamOnly: false },
	{ value: '$349 Website Prestige Upgrade', label: '$349 Website Prestige Upgrade', successTeamOnly: false },
	{ value: 'High Risk Save', label: 'High Risk Save', successTeamOnly: false },
	{ value: 'Pending Cancel Save', label: 'Pending Cancel Save', successTeamOnly: false },
	{ value: 'Standalone Website', label: 'Standalone Website', successTeamOnly: false },
	{ value: 'Non-website', label: 'Non-website', successTeamOnly: false },
	{ value: 'Add-on only', label: 'Add-on only', successTeamOnly: false },
	{ value: 'Success Team', label: 'Success Team', successTeamOnly: true },
] as const;

export const WEBSITE_UPGRADE_OPTIONS = [
	{ value: '$249 Website Essential Upgrade', label: '$249 Website Essential Upgrade' },
	{ value: '$299 Website Preferred Upgrade', label: '$299 Website Preferred Upgrade' },
	{ value: '$349 Website Prestige Upgrade', label: '$349 Website Prestige Upgrade' },
] as const;

export const URGENCY_OPTIONS = [
	{ value: 'Send to prospect immediately', label: 'Send to prospect immediately' },
	{
		value: 'Hold until we hear from you/manager to send to prospect',
		label: 'Hold until we hear from you/manager to send to prospect',
	},
] as const;

/**
 * Pre-fill data for the Current Client Contract modal, sourced from
 * the Upgrade Wizard's subscription state and preview result.
 * All fields are optional — the rep can overwrite any of them.
 */
export interface CurrentClientContractPrefillData {
	adminLink?: string;
	companyName?: string;
	currentLevitatePlan?: string;
	renewalDate?: string; // YYYY-MM-DD format
	billingCycle?: string; // 'Annual' | 'Quarterly' | 'Monthly'
	totalPriceDueToday?: number;
	fbPremiumChecked?: boolean;
	gLSAChecked?: boolean;
}
